/**
 * Utility for cleaning and normalizing markdown content
 */

// Placeholder tokens for protected content
const MATH_BLOCK_PLACEHOLDER = '⚡MATHBLOCK⚡';
const MATH_INLINE_PLACEHOLDER = '⚡MATHINLINE⚡';
const BOLD_PLACEHOLDER_START = '⚡BOLDSTART⚡';
const BOLD_PLACEHOLDER_END = '⚡BOLDEND⚡';

/**
 * Cleans markdown content by replacing unicode escapes and HTML tags
 * with proper characters while preserving markdown formatting and LaTeX math
 */
export const cleanMarkdownContent = (content: string): string => {
  const normalizedContent = content
    .replace(/\\u202f/g, ' ') // Replace non-breaking thin spaces
    .replace(/\\u2011/g, '-') // Replace non-breaking hyphens
    .replace(/\\u2013/g, '–') // Replace en dashes
    .replace(/\\u2248/g, '≈') // Replace approximation symbol
    .replace(/\\u00d7/g, '×'); // Replace multiplication symbol

  // First, protect LaTeX math expressions from being modified
  const mathBlocks: string[] = [];
  const mathInlines: string[] = [];

  // Protect block math: $$ ... $$ (can span multiple lines)
  let protectedContent = normalizedContent.replace(/\$\$[\s\S]*?\$\$/g, (match) => {
    mathBlocks.push(match);
    return `${MATH_BLOCK_PLACEHOLDER}${mathBlocks.length - 1}${MATH_BLOCK_PLACEHOLDER}`;
  });

  // Protect inline math with double dollars: $$ ... $$ already handled above
  // Single dollar inline math ($...$) is now disabled at the remark-math plugin level
  // (singleDollarTextMath: false) to avoid conflicts with stock tickers like $AMD
  // and price mentions like "$UBS to 130". Use \(...\) for inline math instead.

  // Protect LaTeX commands: \[ ... \] and \( ... \)
  protectedContent = protectedContent.replace(/\\\[[\s\S]*?\\\]/g, (match) => {
    mathBlocks.push(match);
    return `${MATH_BLOCK_PLACEHOLDER}${mathBlocks.length - 1}${MATH_BLOCK_PLACEHOLDER}`;
  });
  protectedContent = protectedContent.replace(/\\\([\s\S]*?\\\)/g, (match) => {
    mathInlines.push(match);
    return `${MATH_INLINE_PLACEHOLDER}${mathInlines.length - 1}${MATH_INLINE_PLACEHOLDER}`;
  });

  // Strip italic markdown syntax while preserving bold formatting
  const cleanedContent = protectedContent
    // First handle bold to prevent interference with italic removal
    .replace(/\*\*([^*]+)\*\*/g, `${BOLD_PLACEHOLDER_START}$1${BOLD_PLACEHOLDER_END}`) // Temporarily replace bold **text**
    .replace(/__([^_]+)__/g, `${BOLD_PLACEHOLDER_START}$1${BOLD_PLACEHOLDER_END}`) // Temporarily replace bold __text__
    // Now safely remove italics (with word boundaries to avoid breaking bullet points)
    .replace(/(^|\s)\*([a-zA-Z][^*]*?)\*(?=[\s.,!?;:]|$)/gm, '$1$2') // Remove italic *text* but preserve bullets
    .replace(/(^|\s)_([a-zA-Z][^_]*?)_(?=[\s.,!?;:]|$)/gm, '$1$2') // Remove italic _text_ but preserve underscores
    // Restore bold formatting
    .replace(new RegExp(`${BOLD_PLACEHOLDER_START}([^⚡]+)${BOLD_PLACEHOLDER_END}`, 'g'), '**$1**') // Restore bold as **text**
    // Escape tildes to prevent accidental markdown strikethrough (but not inside math)
    .replace(/~/g, '\\~'); // Escape ~ to prevent ~~text~~ strikethrough

  // Check if content contains table markdown
  const hasTable = cleanedContent.includes('|') && cleanedContent.includes('---');

  // Helper function to restore protected math expressions
  const restoreMathExpressions = (text: string): string => {
    let result = text;
    // Restore block math expressions
    result = result.replace(
      new RegExp(`${MATH_BLOCK_PLACEHOLDER}(\\d+)${MATH_BLOCK_PLACEHOLDER}`, 'g'),
      (_, index) => mathBlocks[parseInt(index, 10)] || '',
    );
    // Restore inline math expressions
    result = result.replace(
      new RegExp(`${MATH_INLINE_PLACEHOLDER}(\\d+)${MATH_INLINE_PLACEHOLDER}`, 'g'),
      (_, index) => mathInlines[parseInt(index, 10)] || '',
    );
    return result;
  };

  if (hasTable) {
    // For tables, preserve <br> tags as-is for proper rendering
    const tableResult = cleanedContent
      .replace(/\\n/g, '\n') // Convert literal \n to actual newlines
      .replace(/\n{3,}/g, '\n\n'); // Normalize excessive newlines
    return restoreMathExpressions(tableResult);
  }

  // For non-table content, convert <br> to markdown line breaks
  const result = cleanedContent
    .replace(/<br\s*\/?>/gi, '  \n') // Replace <br> with markdown line breaks
    .replace(/\\n/g, '  \n') // Ensure proper markdown line breaks
    .replace(/\n{3,}/g, '\n\n'); // Normalize excessive newlines to just two
  return restoreMathExpressions(result);
};

/**
 * Strips all markdown formatting to produce plain text.
 * Useful for previews and text-only displays.
 */
export const stripMarkdown = (content: string): string => {
  return (
    content
      // Remove images first: ![alt](url) -> alt (must come before links)
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
      // Remove links but keep text: [text](url) -> text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove bold/italic: **text**, __text__, *text*, _text_
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
      // Remove strikethrough: ~~text~~
      .replace(/~~([^~]+)~~/g, '$1')
      // Remove inline code: `code`
      .replace(/`([^`]+)`/g, '$1')
      // Remove headers: # Header
      .replace(/^#{1,6}\s+/gm, '')
      // Remove blockquotes: > quote
      .replace(/^>\s+/gm, '')
      // Remove horizontal rules
      .replace(/^[-*_]{3,}\s*$/gm, '')
      // Remove list markers: - item, * item, 1. item
      .replace(/^[\s]*[-*+]\s+/gm, '')
      .replace(/^[\s]*\d+\.\s+/gm, '')
      // Clean up extra whitespace
      .replace(/\n{2,}/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  );
};

/**
 * Strip trailing incomplete math block fences (streaming artifact).
 *
 * Counts only $$ tokens that appear outside fenced code blocks (``` / ~~~)
 * and outside inline code spans (`...`), because remark-math ignores $$ in
 * those contexts and we must not truncate non-math content that contains $$.
 *
 * If there is an odd number of such outer $$ tokens the last one is an
 * unclosed math fence that will crash remark-math's exitMathFlowFence —
 * remove it by slicing the content at that position.
 */
export const stripIncompleteMathFences = (content: string): string => {
  const lines = content.split('\n');
  let inCodeFence = false;
  let mathDelimCount = 0;
  let lastDelimPos = -1;
  let charPos = 0;

  for (const line of lines) {
    // Toggle code-fence state on ``` / ~~~ lines (remark-math skips these).
    if (/^(`{3,}|~{3,})/.test(line)) {
      inCodeFence = !inCodeFence;
    } else if (!inCodeFence) {
      // Scan for $$ outside code blocks, skipping inline code spans.
      let i = 0;
      let inInlineCode = false;
      while (i < line.length) {
        if (line[i] === '`') {
          inInlineCode = !inInlineCode;
          i++;
        } else if (!inInlineCode && line[i] === '$' && line[i + 1] === '$') {
          mathDelimCount++;
          lastDelimPos = charPos + i;
          i += 2;
        } else {
          i++;
        }
      }
    }
    charPos += line.length + 1; // +1 for the '\n' separator
  }

  // Odd count → last delimiter is unclosed → strip from there
  if (mathDelimCount % 2 === 1) {
    return content.slice(0, lastDelimPos);
  }
  return content;
};
